import React from 'react';
import { Box } from '@mui/material';
import SparkOptionsModal from 'components/common/SparkOptionsModal';
import { ReviewFormModalValues } from 'components/common/ReviewFormModal';
import {
  useCreateFavouriteList,
  useCreateMemoryFavourite,
  useDeleteMemoryFavourite,
} from 'hooks/Favourites';
import { useAddUserFlag } from 'hooks/userFlags/useAddUserFlag';
import { useFlagReasons } from 'hooks/EntityTypes/useFlagReasons';
import { useMemoryByID } from 'hooks/memory/useMemoryByID';
import { useAddMemoryRating, useUpdateMemoryRating } from 'hooks/memory';
import { useCurrentUser } from 'hooks/common';
import type { Memory } from 'types/Memory';
import type { CreateFavouriteListFormValues } from 'components/common/Favourite/CreateFavouriteListPopUp';
import type { SparkReportFormValues } from 'components/common/Report/SparkReport';
import { useUpdateMemory } from 'hooks/memory/useUpdateMemory';
import { getCurrentDateISOString } from 'utilities/time';

type MemoryRowOptionsProps = {
  memory: Memory | undefined;
  isParentLoading: boolean;
};
function MemoryRowOptions({ memory, isParentLoading }: MemoryRowOptionsProps) {
  const memoryID = memory?.id || '';
  const { currentUserID } = useCurrentUser();
  const { mutateAsync: addMemoryRating } = useAddMemoryRating(memoryID);
  const { joinedMemory } = useMemoryByID(memoryID, {
    enabled: !isParentLoading && !!memoryID,
  });
  const { ratings, permissions } = joinedMemory?.meta.user || {};
  const allowedToReview = permissions?.canReview;
  const userRating = ratings?.find((rating) => rating.memoryID === memoryID);
  const { mutateAsync: updateMemoryRating } = useUpdateMemoryRating(
    userRating?.id ? `${userRating?.id}` : undefined,
    memoryID
  );
  const { mutateAsync: createFavoriteList } = useCreateFavouriteList();
  const { mutateAsync: addUserFlag } = useAddUserFlag();
  const { dropdownOptions: flagReasonsDropdownOptions } = useFlagReasons();
  const { mutateAsync: updateMemory } = useUpdateMemory(memoryID);
  const {
    mutateAsync: createMemoryFavorites,
    isLoading: isCreatingMemoryFavourite,
  } = useCreateMemoryFavourite(memoryID);
  const {
    mutateAsync: deleteMemoryFavorites,
    isLoading: isDeletingMemoryFavourite,
  } = useDeleteMemoryFavourite(memoryID);

  const existingFavouriteList = joinedMemory?.meta.user.favouriteLists;
  const memoryFavourites = joinedMemory?.memoryFavourite;

  const handleCreateList = async (values: CreateFavouriteListFormValues) => {
    const res = await createFavoriteList(values.listName);

    await createMemoryFavorites({
      memoryID: joinedMemory?.id,
      userID: currentUserID,
      favouriteListID: Number(res.data.id),
    });
  };
  const handleFavouriteListItem = async (listID: number) => {
    const foundExistingFavouriteList = memoryFavourites?.find(
      (favourite) => favourite.attributes.favouriteListID === listID
    );
    if (foundExistingFavouriteList) {
      await deleteMemoryFavorites(`${foundExistingFavouriteList.id}`);
      return;
    }
    await createMemoryFavorites({
      memoryID: joinedMemory?.id,
      userID: currentUserID,
      favouriteListID: listID,
    });
  };

  const handleSendReport = async (values: SparkReportFormValues) => {
    const userFlag = {
      flagReasonID: +values.reasonID,
      memoryID: joinedMemory?.id,
      comments: values.comments,
    };
    await addUserFlag(userFlag);
  };

  const handleDeleteMemory = async () => {
    await updateMemory({
      deletedAt: getCurrentDateISOString(),
      isDeleted: true,
    });
  };

  const handleArchiveMemory = async () => {
    await updateMemory({
      isArchived: true,
    });
  };
  const userPermissions = joinedMemory?.meta.user.permissions;

  const isLoadingMemoryFavourites =
    isCreatingMemoryFavourite || isDeletingMemoryFavourite;

  const handleUpdateMemoryRating = async (values: ReviewFormModalValues) => {
    const doesReviewExists = !!userRating;
    if (!doesReviewExists) {
      await addMemoryRating({ ...values, memoryID });
    }
    if (doesReviewExists) {
      await updateMemoryRating({ ...values, memoryID });
    }
  };

  return (
    <Box
      display='flex'
      alignItems='center'
      gap={2}
      justifyContent='flex-end'
      width='100%'
    >
      <>
        <SparkOptionsModal
          resourceLogoURL={joinedMemory?.attributes.iconURL || ''}
          options={joinedMemory?.meta.options}
          resourceID={joinedMemory?.id}
          privacyTypeId={joinedMemory?.attributes.memoryPrivacyTypeID}
          resourceAccessLinkToken={joinedMemory?.attributes.accessLinkToken}
          handleSparkRatingSave={handleUpdateMemoryRating}
          userRating={userRating}
          allowedToReview={allowedToReview}
          resourceType='memory'
          onFavouriteItemClick={handleFavouriteListItem}
          existingFavouriteList={existingFavouriteList || []}
          onCreateList={handleCreateList}
          isLoading={isLoadingMemoryFavourites}
          onDelete={handleDeleteMemory}
          onArchive={handleArchiveMemory}
          flagReasonsDropdownOptions={flagReasonsDropdownOptions}
          onSendReport={handleSendReport}
          isResourceFavourite={joinedMemory?.meta.user.isFavourited}
          showHeartIcon={false}
          showShareIcon={false}
          userPermissions={userPermissions}
          resourceName={joinedMemory?.attributes?.name || '- -'}
        />
      </>
    </Box>
  );
}

export default MemoryRowOptions;
